import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Apple,
  RadioTower,
  Target,
  Wrench,
  CheckCircle2,
  XCircle,
  TimerOff,
  Ban,
  Loader2,
  Play,
  Square,
  WifiOff,
  Wifi,
  KeyRound,
} from "lucide-react";
import { useMacAgentCaptureJob } from "@/modules/collector_v2/hooks/useMacAgentCaptureJob";

const ADMIN_TOKEN_STORAGE_KEY = "ppk_admin_token";

const STATE_META = {
  idle: {
    Icon: Apple,
    color: "text-slate-500",
    label: "Listo",
    desc: "Primero iniciá la captura. Cuando aparezca Escuchando tu Mac, iniciá sesión con tu cuenta observadora en PPPoker.",
  },
  pending: {
    Icon: Loader2,
    color: "text-blue-500 animate-spin",
    label: "Esperando agente",
    desc: "Job creado. Esperando que el daemon de tu Mac lo levante (≤60s)…",
  },
  capturing: {
    Icon: RadioTower,
    color: "text-emerald-500 animate-pulse",
    label: "🟢 Escuchando tu Mac",
    desc:
      "Captura activa para Observer 02. Iniciá sesión con el UID 13551134 en esa instancia de la Mac. Si ya estaba abierta, cerrá y volvé a abrir esa instancia; si no se detecta el login, cerrá sesión y volvé a ingresar.",
  },
  detected: {
    Icon: Target,
    color: "text-cyan-500",
    label: "RDKEY detectado",
    desc: "Llegó UserLoginREQ del cliente. Procesando…",
  },
  applying: {
    Icon: Wrench,
    color: "text-blue-500 animate-pulse",
    label: "Aplicando",
    desc: "Actualizando configuración del scout en el VPS…",
  },
  completed: {
    Icon: CheckCircle2,
    color: "text-emerald-500",
    label: "Credencial recibida",
    desc: "Revisá el UID. La conexión se confirmará al iniciar Collector; capturar no lo activa.",
  },
  failed: {
    Icon: XCircle,
    color: "text-red-500",
    label: "Error",
    desc: "Algo salió mal. Detalles abajo.",
  },
  expired: {
    Icon: TimerOff,
    color: "text-zinc-500",
    label: "Tiempo agotado",
    desc:
      "Se agotó la ventana sin captura. Verificá que el agente esté corriendo en tu Mac y reintentá.",
  },
  cancelled: {
    Icon: Ban,
    color: "text-zinc-500",
    label: "Cancelado",
    desc: "Captura cancelada por el operador.",
  },
};

const TERMINAL = new Set(["completed", "failed", "expired", "cancelled"]);

const fmtETA = (expires_at) => {
  if (!expires_at) return null;
  const remaining = Math.max(
    0,
    Math.floor((new Date(expires_at) - new Date()) / 1000)
  );
  const mm = String(Math.floor(remaining / 60)).padStart(1, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

export const MacAgentCaptureWizard = ({ open, onClose, onSuccess }) => {
  const {
    job,
    error,
    starting,
    agentOnline,
    agentLastSeenS,
    agentHostname,
    startJob,
    cancelJob,
    reset,
    fetchActive,
  } = useMacAgentCaptureJob();
  const [tick, setTick] = useState(0);
  const [hasToken, setHasToken] = useState(
    () =>
      typeof window !== "undefined" &&
      !!localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)
  );
  const [tokenInput, setTokenInput] = useState("");

  const saveToken = () => {
    const t = tokenInput.trim();
    if (!t) return;
    try {
      localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, t);
    } catch {
      /* sin localStorage no es bloqueante */
    }
    setHasToken(true);
    setTokenInput("");
    // refrescar estado del agente con el nuevo token
    fetchActive();
  };

  // Reset al cerrar + check agente al abrir + auto-refresh cada 5s
  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (typeof window !== "undefined") {
      setHasToken(!!localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY));
    }
    // fetchActive intentará obtener automáticamente el token vía bridge JWT
    fetchActive().then(() => {
      if (typeof window !== "undefined") {
        setHasToken(!!localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY));
      }
    });
    const id = setInterval(() => {
      if (!job || TERMINAL.has(job.state)) {
        fetchActive();
      }
    }, 5000);
    return () => clearInterval(id);
  }, [open, reset, fetchActive, job]);

  // Countdown
  useEffect(() => {
    if (!job || TERMINAL.has(job.state)) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [job]);

  // onSuccess
  useEffect(() => {
    if (job?.state === "completed" && onSuccess) {
      onSuccess(job);
    }
  }, [job?.state, job, onSuccess]);

  const state = job?.state || "idle";
  const meta = STATE_META[state] || STATE_META.idle;
  const Icon = meta.Icon;
  const isTerminal = TERMINAL.has(state);
  const isActive = job && !isTerminal && state !== "pending";
  const eta = useMemo(
    () => (job && !isTerminal ? fmtETA(job?.expires_at) : null),
    [job, isTerminal, tick] // eslint-disable-line
  );
  const result = job?.result;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-md"
        data-testid="mac-agent-capture-wizard"
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            data-testid="mac-agent-wizard-title"
          >
            <Apple className="w-5 h-5 text-slate-600" />
            Conectar cuenta desde Mac
          </DialogTitle>
          <DialogDescription data-testid="mac-agent-wizard-desc">
            Observer 02 · UID 13551134 · 5 minutos. Esperá el aviso de captura activa y después iniciá sesión en esa instancia.
          </DialogDescription>
        </DialogHeader>

        {/* Estado del agente (heartbeat) */}
        <div
          className={`text-xs flex items-center justify-between gap-2 px-3 py-2 rounded border ${
            agentOnline
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
          data-testid="mac-agent-wizard-agent-status"
        >
          <span className="flex items-center gap-2">
            {agentOnline ? (
              <Wifi className="w-3.5 h-3.5" />
            ) : (
              <WifiOff className="w-3.5 h-3.5" />
            )}
            Agente: {agentOnline ? "online" : "offline"}
            {agentHostname && (
              <span className="font-mono text-[10px] opacity-75">
                ({agentHostname})
              </span>
            )}
          </span>
          {agentLastSeenS !== null && (
            <span className="font-mono text-[10px] opacity-70">
              hace {Math.round(agentLastSeenS)}s
            </span>
          )}
        </div>

        {/* Setup del admin token (solo si no está guardado) */}
        {!hasToken && state === "idle" && (
          <div
            className="text-xs bg-amber-50 border border-amber-200 rounded p-3 space-y-2"
            data-testid="mac-agent-wizard-token-setup"
          >
            <div className="flex items-center gap-2 text-amber-800">
              <KeyRound className="w-3.5 h-3.5" />
              <strong>Pegá tu Admin Token (solo la primera vez)</strong>
            </div>
            <div className="text-amber-700 text-[11px]">
              Es el mismo token que usás en "Refrescar rdkey". Se guarda en
              localStorage de este navegador.
            </div>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="X-Admin-Token…"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveToken()}
                className="h-8 text-xs"
                data-testid="mac-agent-wizard-token-input"
              />
              <Button
                size="sm"
                onClick={saveToken}
                disabled={!tokenInput.trim()}
                className="h-8 px-3 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="mac-agent-wizard-token-save"
              >
                Guardar
              </Button>
            </div>
          </div>
        )}

        {/* Estado visual principal */}
        <div
          className="flex flex-col items-center justify-center py-6 px-2 text-center bg-zinc-50 rounded-lg border border-zinc-200"
          data-testid="mac-agent-wizard-state-box"
        >
          <Icon
            className={`w-12 h-12 ${meta.color}`}
            data-testid={`mac-agent-wizard-icon-${state}`}
          />
          <div
            className="mt-3 text-sm font-semibold text-zinc-900"
            data-testid="mac-agent-wizard-state-label"
          >
            {meta.label}
          </div>
          <div
            className="mt-1 text-xs text-zinc-600 max-w-[28rem]"
            data-testid="mac-agent-wizard-state-desc"
          >
            {meta.desc}
          </div>
          {eta && (
            <div
              className="mt-3 text-xs font-mono text-zinc-700"
              data-testid="mac-agent-wizard-eta"
            >
              ⏱ Tiempo restante: <span className="font-bold">{eta}</span>
            </div>
          )}
        </div>

        {/* Resultado al completar */}
        {state === "completed" && result && (
          <div
            className="text-xs bg-emerald-50 border border-emerald-200 rounded p-3 space-y-1 text-emerald-900"
            data-testid="mac-agent-wizard-result"
          >
            <div>
              Acción: <code className="font-mono">{result.action}</code>
            </div>
            <div>
              UID: <code className="font-mono">{result.uid}</code>
            </div>
            {result.action === "rotated" && result.scout && (
              <div>
                Scout rotado: <code className="font-mono">{result.scout}</code>{" "}
                ✅
              </div>
            )}
            {result.action === "pending_new" && (
              <div>
                UID nuevo → guardado para aprobación manual abajo en la lista
                de pendientes ⬇
              </div>
            )}
            <div className="opacity-80">
              RDKEY:{" "}
              <code className="font-mono">{result.rdkey_prefix}…</code>
            </div>
          </div>
        )}

        {/* Error */}
        {(error || job?.error) && (
          <div
            className="text-xs bg-red-50 border border-red-300 rounded p-3 text-red-900"
            data-testid="mac-agent-wizard-error"
          >
            <strong>Error:</strong>{" "}
            {typeof error === "object" ? JSON.stringify(error) : error || job?.error}
          </div>
        )}

        {/* Log timeline */}
        {job?.log && job.log.length > 0 && (
          <div
            className="max-h-32 overflow-y-auto text-[11px] bg-zinc-100 rounded p-2 font-mono space-y-1"
            data-testid="mac-agent-wizard-log"
          >
            {job.log.slice(-8).map((l, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-zinc-500">
                  {new Date(l.ts).toLocaleTimeString()}
                </span>
                <span className="font-semibold text-zinc-700">
                  [{l.state}]
                </span>
                <span className="text-zinc-800">{l.msg}</span>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          {state === "idle" && (
            <>
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="mac-agent-wizard-close-btn"
              >
                Cerrar
              </Button>
              <Button
                onClick={startJob}
                disabled={starting}
                data-testid="mac-agent-wizard-start-btn"
                title={
                  agentOnline
                    ? "Iniciar captura de 5 minutos"
                    : "El agente parece offline — si igual hacés click y no responde, el job expira a los 60s"
                }
              >
                {starting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Iniciando…
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Iniciar captura
                  </>
                )}
              </Button>
            </>
          )}

          {isActive && (
            <Button
              variant="destructive"
              onClick={cancelJob}
              data-testid="mac-agent-wizard-cancel-btn"
            >
              <Square className="w-4 h-4 mr-2" />
              Cancelar captura
            </Button>
          )}

          {isTerminal && (
            <>
              {state !== "completed" && (
                <Button
                  variant="outline"
                  onClick={reset}
                  data-testid="mac-agent-wizard-retry-btn"
                >
                  Reintentar
                </Button>
              )}
              <Button
                onClick={onClose}
                data-testid="mac-agent-wizard-done-btn"
              >
                Cerrar
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MacAgentCaptureWizard;
